import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import { useStringsContext } from '../../../../contexts/strings';
import Stack from '@mui/material/Stack';
import SelectWithValidate, { SelectOptions } from '../../../common/input/select-with-validate';
import { InputWithValidateProps } from '../../../common/input/text-input-with-validate';
import { Option, SUPPORTED_LEAGUES } from '../../../../types';
import { QuizAlternative, QuizQuestion } from '../types';
import Typography from '@mui/material/Typography';
import svStrings from '../../../../sv-strings';
import PollSelectedPlayersLeague from './poll-select-players-league';
import PollSelectedPlayersClub from './poll-select-players-club';

const answerSelection = yup
  .string()
  .nullable()
  .required(svStrings.validations.mustSelectAlternative);

type QuestionPlayerAlternativesKey = keyof Pick<QuizQuestion, 'playerAlternatives'>;
type QuestionAnswerKey = keyof Pick<QuizQuestion, 'answer'>;

const playerAlternativesKey: QuestionPlayerAlternativesKey = 'playerAlternatives';
const answerKey: QuestionAnswerKey = 'answer';

interface Props extends Omit<InputWithValidateProps, 'onValue'> {
  onValue: (key: QuestionPlayerAlternativesKey | QuestionAnswerKey, value: any | null) => void;
  selectedAbbrv: string;
  selectedQuestion: QuizQuestion;
}

const PollQuizPlayerAlternatives = ({
  selectedAbbrv,
  selectedQuestion,
  onValue,
  onValidation,
  hideValidationError,
}: Props) => {
  const { strings } = useStringsContext();

  const isLeague = selectedAbbrv ? selectedAbbrv in SUPPORTED_LEAGUES : false;

  const [selectedPlayers, setSelectedPlayers] = useState<SelectOptions>({
    options: [],
    optionsText: [],
  });

  const [selectedPlayerAnswer, setSelectedPlayerAnswer] = useState<string | undefined>('');

  const initialSelectedPlayers = useMemo(() => {
    const newSelectedPlayersOptions = selectedQuestion.playerAlternatives?.map((alternative) =>
      String(alternative.id)
    );
    const newSelectedPlayersOptionsText = selectedQuestion.playerAlternatives?.map(
      (alternative) => alternative.text
    );

    return {
      options: newSelectedPlayersOptions || [],
      optionsText: newSelectedPlayersOptionsText || [],
    };
  }, [selectedQuestion?.playerAlternatives]);

  useEffect(() => {
    if (selectedQuestion.type === 'player' && !selectedPlayers.options.length) {
      setSelectedPlayerAnswer(
        selectedQuestion.answer ? String(selectedQuestion.answer) : undefined
      );
    }
  }, [
    selectedQuestion,
    selectedQuestion.playerAlternatives,
    selectedQuestion.answer,
    selectedPlayers.options.length,
  ]);

  const handleAnswerValue = useCallback(
    (key: QuestionAnswerKey, value: Option | null) => {
      onValue(key, Number(value));
    },
    [onValue]
  );

  const handleSelectedPlayers = (selectedPlayers: SelectOptions | null) => {
    if (!selectedPlayers) return;
    setSelectedPlayers(selectedPlayers);

    const alternatives: QuizAlternative[] = selectedPlayers.options.map((option, index) => {
      return {
        id: Number(option),
        text: selectedPlayers.optionsText[index],
      };
    });
    onValue && onValue(playerAlternativesKey, alternatives);
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2-bold">{strings.polls.quizAlternativePlayersTitle}</Typography>
      {isLeague ? (
        <PollSelectedPlayersLeague
          selectedAbbrv={selectedAbbrv}
          initialSelectedPlayers={initialSelectedPlayers}
          onSelectedPlayers={handleSelectedPlayers}
          onValidation={onValidation}
          hideValidationError={hideValidationError}
        />
      ) : (
        <PollSelectedPlayersClub
          selectedAbbrv={selectedAbbrv}
          initialSelectedPlayers={initialSelectedPlayers}
          onSelectedPlayers={handleSelectedPlayers}
          onValidation={onValidation}
          hideValidationError={hideValidationError}
          checkboxes
        />
      )}
      <SelectWithValidate
        name={answerKey}
        options={selectedPlayers.options}
        optionsText={selectedPlayers.optionsText}
        value={selectedPlayerAnswer}
        onValue={(key, value) => handleAnswerValue(answerKey, value)}
        validation={answerSelection}
        label={strings.polls.quizSelectPlayerAnswerLabel}
        onValidation={onValidation}
        hideValidationError={hideValidationError}
        disabled={selectedPlayers.options.length === 0}
      />
    </Stack>
  );
};

export default PollQuizPlayerAlternatives;
